import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Search, Filter, Eye, Zap, Star, Users, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TemplateMarketplace = () => {
  const { user, token: authToken } = useAuth();
  
  // State management
  const [templates, setTemplates] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('all');
  const [selectedVariant, setSelectedVariant] = useState('all');
  const [selectedAccessLevel, setSelectedAccessLevel] = useState('all');
  const [sortBy, setSortBy] = useState('popular');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchIndustries();
    fetchTemplates();
  }, [currentPage, selectedIndustry, selectedVariant, selectedAccessLevel, sortBy, searchQuery]);

  const fetchIndustries = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/lead-capture/industries`);
      const data = await response.json();
      if (data.ok) {
        setIndustries(data.industries);
      }
    } catch (error) {
      console.error('Error fetching industries:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        page_size: 12,
        search: searchQuery,
        sort_by: sortBy
      });

      if (selectedIndustry !== 'all') {
        params.append('industry_category', selectedIndustry);
      }
      if (selectedVariant !== 'all') {
        params.append('variant', selectedVariant);
      }
      if (selectedAccessLevel !== 'all') {
        params.append('access_level', selectedAccessLevel);
      }

      const response = await fetch(
        `${BACKEND_URL}/api/lead-capture/templates?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        }
      );
      const data = await response.json();
      
      if (data.ok) {
        setTemplates(data.templates);
        setTotalPages(Math.ceil(data.total_count / 12));
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error("Failed to load templates. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSelect = async (template) => {
    try {
      // Navigate to template customization
      window.location.href = `/member/lead-capture/generate?template=${template.id}`;
    } catch (error) {
      console.error('Error selecting template:', error);
      toast.error("Failed to select template. Please try again.");
    }
  };

  const handlePreviewTemplate = async (template) => {
    try {
      // Open template preview in new window
      const response = await fetch(
        `${BACKEND_URL}/api/lead-capture/templates/${template.id}`,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        }
      );
      const data = await response.json();
      if (data.ok && data.template.preview_url) {
        window.open(data.template.preview_url, '_blank');
      } else {
        toast("Template preview is not available yet.");
      }
    } catch (error) {
      console.error('Error previewing template:', error);
    }
  };

  const getVariantBadgeColor = (variant) => {
    const colors = {
      'modern': 'bg-blue-100 text-blue-800',
      'classic': 'bg-green-100 text-green-800',
      'bold': 'bg-purple-100 text-purple-800',
      'minimal': 'bg-gray-100 text-gray-800'
    };
    return colors[variant] || 'bg-gray-100 text-gray-800';
  };

  const getAccessLevelBadge = (accessLevel) => {
    const levels = {
      'boot_camp': { label: 'Boot Camp', color: 'bg-yellow-100 text-yellow-800' },
      'rookie': { label: 'Rookie', color: 'bg-blue-100 text-blue-800' },
      'pro': { label: 'Pro', color: 'bg-purple-100 text-purple-800' },
      'enterprise': { label: 'Enterprise', color: 'bg-red-100 text-red-800' }
    };
    const level = levels[accessLevel] || { label: accessLevel, color: 'bg-gray-100 text-gray-800' };
    return level;
  };

  return (
    <div className="container mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Template Marketplace</h1>
        <p className="text-gray-600">Choose from professional lead capture templates designed to convert visitors into leads</p>
      </div>

      {/* Stats Banner */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center space-x-2">
              <Zap className="h-5 w-5 text-blue-500" />
              <span className="text-2xl font-bold text-gray-900">{templates.length}</span>
            </div>
            <p className="text-sm text-gray-600 mt-1">Templates Available</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center space-x-2">
              <Users className="h-5 w-5 text-green-500" />
              <span className="text-2xl font-bold text-gray-900">{industries.length}</span>
            </div>
            <p className="text-sm text-gray-600 mt-1">Industries</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center space-x-2">
              <Star className="h-5 w-5 text-yellow-500" />
              <span className="text-2xl font-bold text-gray-900">{templates.filter(t => t.popular).length}</span>
            </div>
            <p className="text-sm text-gray-600 mt-1">Popular Templates</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="flex items-center justify-center space-x-2">
              <TrendingUp className="h-5 w-5 text-purple-500" />
              <span className="text-2xl font-bold text-gray-900">{user?.membership_tier || 'Free'}</span>
            </div>
            <p className="text-sm text-gray-600 mt-1">Your Access Level</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            {/* Search */}
            <div className="md:col-span-2 relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Industry Filter */}
            <div>
              <Select value={selectedIndustry} onValueChange={setSelectedIndustry}>
                <SelectTrigger>
                  <SelectValue placeholder="All Industries" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Industries</SelectItem>
                  {industries.map((industry) => (
                    <SelectItem key={industry.id} value={industry.category}>
                      {industry.name} ({industry.template_count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Variant Filter */}
            <div>
              <Select value={selectedVariant} onValueChange={setSelectedVariant}>
                <SelectTrigger>
                  <SelectValue placeholder="All Variants" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Variants</SelectItem>
                  <SelectItem value="modern">Modern</SelectItem>
                  <SelectItem value="classic">Classic</SelectItem>
                  <SelectItem value="bold">Bold</SelectItem>
                  <SelectItem value="minimal">Minimal</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Access Level Filter */}
            <div>
              <Select value={selectedAccessLevel} onValueChange={setSelectedAccessLevel}>
                <SelectTrigger>
                  <SelectValue placeholder="All Levels" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Levels</SelectItem>
                  <SelectItem value="boot_camp">Boot Camp</SelectItem>
                  <SelectItem value="rookie">Rookie</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Sort Filter */}
            <div>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger>
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="popular">Popular First</SelectItem>
                  <SelectItem value="newest">Newest First</SelectItem>
                  <SelectItem value="name">Name A-Z</SelectItem>
                  <SelectItem value="conversion_rate">Best Converting</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Templates Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[...Array(8)].map((_, index) => (
            <Card key={index} className="animate-pulse">
              <CardContent className="p-0">
                <div className="h-48 bg-gray-300 rounded-t-lg"></div>
                <div className="p-4 space-y-3">
                  <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-300 rounded w-full"></div>
                  <div className="h-3 bg-gray-300 rounded w-5/6"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {templates.map((template) => {
            const industry = industries.find(i => i.category === template.industry_category);
            const accessLevel = getAccessLevelBadge(template.access_level);
            
            return (
              <Card key={template.id} className="group hover:shadow-lg transition-shadow cursor-pointer">
                <CardContent className="p-0">
                  {/* Template Preview */}
                  <div className="relative h-48 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-t-lg overflow-hidden">
                    {template.thumbnail_path ? (
                      <img 
                        src={template.thumbnail_path} 
                        alt={template.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <div className="text-center">
                          <Zap className="h-12 w-12 text-blue-500 mx-auto mb-2" />
                          <p className="text-sm text-gray-600">{template.title}</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity space-x-2">
                        <Button 
                          size="sm" 
                          variant="secondary"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePreviewTemplate(template);
                          }}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          Preview
                        </Button>
                        <Button 
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTemplateSelect(template);
                          }}
                        >
                          <Zap className="h-4 w-4 mr-1" />
                          Use Template
                        </Button>
                      </div>
                    </div>

                    {/* Badges */}
                    <div className="absolute top-2 left-2 space-y-1">
                      {template.featured && (
                        <Badge className="bg-yellow-500 text-white">Featured</Badge>
                      )}
                      {template.popular && (
                        <Badge className="bg-green-500 text-white">Popular</Badge>
                      )}
                    </div>

                    <div className="absolute top-2 right-2">
                      <Badge className={accessLevel.color}>{accessLevel.label}</Badge>
                    </div>
                  </div>

                  {/* Template Info */}
                  <div className="p-4 space-y-3">
                    <div>
                      <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                        {template.title}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">{template.description}</p>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Badge variant="outline" className={getVariantBadgeColor(template.variant)}>
                          {template.variant}
                        </Badge>
                        {industry && (
                          <Badge variant="outline" style={{ backgroundColor: `${industry.color_theme}20`, color: industry.color_theme }}>
                            {industry.name}
                          </Badge>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <span>{template.usage_count || 0} uses</span>
                      {template.conversion_rate && (
                        <span>{(template.conversion_rate * 100).toFixed(1)}% conversion</span>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Empty State */}
      {!loading && templates.length === 0 && (
        <div className="text-center py-12">
          <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No templates found</h3>
          <p className="text-gray-600">Try adjusting your filters or search terms</p>
          <Button 
            className="mt-4" 
            variant="outline"
            onClick={() => {
              setSearchQuery('');
              setSelectedIndustry('all');
              setSelectedVariant('all');
              setSelectedAccessLevel('all');
            }}
          >
            Clear Filters
          </Button>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-8 space-x-2">
          <Button 
            variant="outline" 
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            Previous
          </Button>
          
          {[...Array(totalPages)].map((_, index) => (
            <Button
              key={index}
              variant={currentPage === index + 1 ? "default" : "outline"}
              onClick={() => setCurrentPage(index + 1)}
            >
              {index + 1}
            </Button>
          ))}
          
          <Button 
            variant="outline" 
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
};

export default TemplateMarketplace;